/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
import { init } from './internal/i18n.js';
/**
 * Return a localized message.
 *
 * @param locale The supported locale.
 * @param message The message key.
 * @param parameters The paramters needed to build the message.
 * @returns the localized message.
 */
export const localize = init({
    // prettier-ignore
    fr_FR: {
        'ai.chat.cancel': 'Annuler',
        'ai.chat.close': 'Fermer',
        'ai.chat.collapse': 'Réduire',
        'ai.chat.copied-to-clipboard': 'Copié dans le presse-papiers',
        'ai.chat.copy': 'Copier',
        'ai.chat.error.connectivity-issues.description': 'Problèmes de connectivité.',
        'ai.chat.error.connectivity-issues': "Vous semblez être hors ligne. Veuillez vérifier votre connexion réseau et réessayer. Une fois reconnecté, vous pourrez poursuivre votre conversation.",
        'ai.chat.error.error-id': "ID de l'erreur: {}",
        'ai.chat.error.generating-response.description': "Veuillez attendre avant d'envoyer un nouveau message.",
        'ai.chat.error.generating-response': 'Réponse en cours',
        'ai.chat.error.internal-server-error.description': "Nous rencontrons un problème. Veuillez réessayer. Si le problème persiste, contactez le service d'assistance de PROS.",
        'ai.chat.error.internal-server-error': 'Erreur de serveur interne',
        'ai.chat.error.invalid-request.description': "Il semble qu'il y ait un problème avec les informations fournies. Veuillez revoir votre demande et réessayer. Si le problème persiste, contactez le service d'assistance de PROS.",
        'ai.chat.error.invalid-request': 'Demande non valide',
        'ai.chat.error.network-error': 'Problème de connection, veuillez réessayer plus tard.',
        'ai.chat.error.query-failure': "Quelque chose n'a pas fonctionné. Veuillez réessayer plus tard.",
        'ai.chat.error.unknown-custom-renderer': 'Type de rendu personnalisé inconnu.',
        'ai.chat.expand': 'Agrandir le Chat',
        'ai.chat.feedback-provided.subtitle': 'Votre contribution est importante - merci de nous aider à améliorer PROS AI.',
        'ai.chat.feedback-provided.title': 'Merci pour vos commentaires !',
        'ai.chat.fullscreen': 'Plein Ecran',
        'ai.chat.history-disclaimer': "PROS AI est encore en phase d'apprentissage et peut faire des erreurs. L'historique des conversations est conservé jusqu'à ce que vous vous déconnectiez.",
        'ai.chat.minimize': 'Réduire',
        'ai.chat.new-chat': 'Démarrer une nouvelle discussion',
        'ai.chat.pros-ai': 'PROS AI',
        'ai.chat.provide-feedback.input-label': 'Que pouvons-nous améliorer ?',
        'ai.chat.provide-feedback.input-placeholder': 'Votre avis nous intéresse - faites-nous savoir ce que vous en pensez…',
        'ai.chat.provide-feedback': 'Donnez votre avis sur PROS AI',
        'ai.chat.reaction.already-provided': "Retour d'information déjà fourni",
        'ai.chat.reaction.negative': 'Pas Utile',
        'ai.chat.reaction.positive': 'Utile',
        'ai.chat.retry': 'Réessayer',
        'ai.chat.submit': 'Envoyer',
        'ai.chat.suggested-prompts': "Suggestions d'idées",
        'ai.chat.type-a-message': 'Tapez un message…',
        'ai.chat.waiting-for-response': 'Nous travaillons sur une réponse… Veuillez patienter.',
        'ai.chat.loading.message.one': 'Analyse de votre demande…',
        'ai.chat.loading.message.two': 'Réflexion en cours…',
        'ai.chat.loading.message.three': 'Traitement de votre demande…',
        'ai.chat.loading.message.four': 'Synthèse des résultats…',
    },
    // prettier-ignore
    en_US: {
        'ai.chat.cancel': 'Cancel',
        'ai.chat.close': 'Close',
        'ai.chat.collapse': 'Collapse',
        'ai.chat.copied-to-clipboard': 'Copied to clipboard',
        'ai.chat.copy': 'Copy',
        'ai.chat.error.connectivity-issues.description': 'You appear to be offline. Please check your network connection and try again. Once reconnected, you can continue your conversation.',
        'ai.chat.error.connectivity-issues': 'Connectivity Issues',
        'ai.chat.error.error-id': 'Error ID: {}',
        'ai.chat.error.generating-response.description': 'Please wait before sending a new message.',
        'ai.chat.error.generating-response': 'Generating response',
        'ai.chat.error.internal-server-error.description': "We’re experiencing an issue on our side. Please try again shortly. If the problem persists, contact PROS Support.",
        'ai.chat.error.internal-server-error': 'Internal Server Error',
        'ai.chat.error.invalid-request.description': 'It looks like there’s an issue with the information provided. Please review your request and try again. If the problem persists, contact PROS Support.',
        'ai.chat.error.invalid-request': 'Invalid Request',
        'ai.chat.error.network-error': 'Network error, please try again later.',
        'ai.chat.error.query-failure': 'Something went wrong. Please try again later.',
        'ai.chat.error.unknown-custom-renderer': 'Unknown custom render type.',
        'ai.chat.expand': 'Expand',
        'ai.chat.feedback-provided.subtitle': 'Your input matters - thank you for helping us improve PROS AI.',
        'ai.chat.feedback-provided.title': 'Thank you for your feedback!',
        'ai.chat.fullscreen': 'Full screen',
        'ai.chat.history-disclaimer': 'PROS AI is still learning and can make mistakes. Chat history is stored until you logout.',
        'ai.chat.minimize': 'Minimize',
        'ai.chat.new-chat': 'Start new chat',
        'ai.chat.pros-ai': 'PROS AI',
        'ai.chat.provide-feedback.input-label': 'What can we improve?',
        'ai.chat.provide-feedback.input-placeholder': 'We value your input - let us know what you think…',
        'ai.chat.provide-feedback': 'Provide Feedback about PROS AI',
        'ai.chat.reaction.already-provided': 'Feedback Already Provided',
        'ai.chat.reaction.negative': 'Not Helpful',
        'ai.chat.reaction.positive': 'Helpful',
        'ai.chat.retry': 'Retry',
        'ai.chat.submit': 'Submit',
        'ai.chat.suggested-prompts': 'Suggested Prompts',
        'ai.chat.type-a-message': 'Type a message…',
        'ai.chat.waiting-for-response': 'Working on a response… Please wait.',
        'ai.chat.loading.message.one': 'Analyzing your request…',
        'ai.chat.loading.message.two': 'Thinking…',
        'ai.chat.loading.message.three': 'Working on it…',
        'ai.chat.loading.message.four': 'Synthesizing results…',
    },
});
//# sourceMappingURL=translations.js.map